import { onMounted, watch, type Ref } from "vue";
import { useIconStore, type IconStore } from "@/runtime/composables/useIconStore";
import { fetchFromProviders } from "@/runtime/composables/iconProviders";
import { iconClasses } from "./icon.styles";
import type { IconProps } from "./icon.types";

export interface UseIconOptions {
  props: Readonly<IconProps>;
  iconEl: Ref<HTMLElement | null>;
}

export const useIcon = (options: UseIconOptions) => {
  const { props, iconEl } = options;
  const iconStore: IconStore = useIconStore();

  /**
   * Loads and renders the current icon SVG into the icon container.
   *
   * - Resolves the icon name via {@link IconStore.resolveIconName}
   * - Fetches SVG content via {@link IconStore.fetchSvgContent}
   * - Normalizes accessibility attributes and removes fixed sizing
   */
  const loadSvg = async (): Promise<void> => {
    const el = iconEl.value;
    const name = props.name;

    if (!el || !name) return;

    const iconName = iconStore.resolveIconName(name);
    const svgContent = iconName ? await iconStore.fetchSvgContent(iconName) : await fetchFromProviders(name);
    if (!svgContent) {
      el.innerHTML = "";
      return;
    }

    // SVG content originates from the project-controlled CDN or developer-registered
    // providers (registered at build/app-init time, not by end users). Untrusted
    // user input never reaches this path. If provider content from arbitrary sources
    // is ever supported, replace this with DOMPurify.sanitize(svgContent, { USE_PROFILES: { svg: true } }).
    // nosemgrep: javascript.audit.detect-replaceall-sanitization.detect-replaceall-sanitization
    const label = name
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");

    // Mutate only the root <svg> opening tag: inject a11y attrs and strip fixed
    // dimensions. Non-global match leaves child element dimensions (e.g. <rect width="...">) intact.
    const prepared = svgContent.replace(/<svg\b([^>]*)>/, (_, attrs: string) => {
      const strippedAttrs = attrs.replace(/ width="[^"]*"/, "").replace(/ height="[^"]*"/, "");
      return `<svg role="img" aria-label="${label}" preserveAspectRatio="xMidYMid meet"${strippedAttrs}>`;
    });

    el.innerHTML = prepared;
  };

  onMounted(async () => {
    await iconStore.fetchIconList();
    await loadSvg();
  });

  watch(
    () => props.name,
    () => {
      void loadSvg();
    },
  );

  const classes = iconClasses(props);

  return { classes };
};
